/**
 * Mock ticket data for the SRE Dashboard ticket management feature.
 *
 * Provides realistic demo data aligned with the SkyTech Corp customer scenario.
 */
import type { Ticket, TicketId, IncidentId } from '../types';

function id<T>(value: string): T {
  return value as unknown as T;
}

export const MOCK_TICKETS: readonly Ticket[] = [
  {
    id: id<TicketId>('tkt-001'),
    ticketNumber: 'TKT-2026-0001',
    title: 'payment-service CPU 高負荷の根本原因調査',
    description: 'INC-2026-0001 に関連する payment-service の CPU 使用率異常について、根本原因を特定し恒久対策を実施する。メモリリーク疑いのプロファイリング結果を確認中。',
    status: 'in_progress',
    priority: 'critical',
    category: 'incident_followup',
    assignee: '山田太郎',
    reporter: '佐藤花子',
    relatedIncidentId: id<IncidentId>('inc-001'),
    tags: ['payment', 'performance', 'production'],
    comments: [
      { id: 'cmt-001', author: '山田太郎', content: 'プロファイリング結果を確認しました。GC 設定の見直しが必要です。', createdAt: '2026-02-22T10:30:00Z' },
      { id: 'cmt-002', author: '佐藤花子', content: 'JVM パラメータの変更 PR を作成しました。レビューお願いします。', createdAt: '2026-02-22T14:00:00Z' },
    ],
    dueDate: '2026-02-28T00:00:00Z',
    createdAt: '2026-02-21T11:00:00Z',
    updatedAt: '2026-02-22T14:00:00Z',
    resolvedAt: null,
  },
  {
    id: id<TicketId>('tkt-002'),
    ticketNumber: 'TKT-2026-0002',
    title: 'ELB ヘルスチェック閾値の見直し',
    description: 'web-frontend の ELB ヘルスチェック失敗が頻発しているため、閾値の見直しとヘルスチェックエンドポイントの改善を行う。',
    status: 'open',
    priority: 'high',
    category: 'change_request',
    assignee: null,
    reporter: '田中一郎',
    relatedIncidentId: id<IncidentId>('inc-002'),
    tags: ['web-frontend', 'elb', 'health-check'],
    comments: [],
    dueDate: '2026-03-05T00:00:00Z',
    createdAt: '2026-02-21T12:00:00Z',
    updatedAt: '2026-02-21T12:00:00Z',
    resolvedAt: null,
  },
  {
    id: id<TicketId>('tkt-003'),
    ticketNumber: 'TKT-2026-0003',
    title: 'CloudWatch アラーム整理・統合',
    description: '重複する CloudWatch アラームを整理し、ノイズを削減する。現在30件のアラーム中、類似パターン12件を統合対象とする。',
    status: 'in_progress',
    priority: 'medium',
    category: 'monitoring',
    assignee: '鈴木次郎',
    reporter: '鈴木次郎',
    relatedIncidentId: null,
    tags: ['cloudwatch', 'monitoring', 'noise-reduction'],
    comments: [
      { id: 'cmt-003', author: '鈴木次郎', content: '統合対象リストを作成しました。レビューお願いします。', createdAt: '2026-02-23T09:00:00Z' },
    ],
    dueDate: '2026-03-10T00:00:00Z',
    createdAt: '2026-02-22T08:00:00Z',
    updatedAt: '2026-02-23T09:00:00Z',
    resolvedAt: null,
  },
  {
    id: id<TicketId>('tkt-004'),
    ticketNumber: 'TKT-2026-0004',
    title: '月次パッチ適用スケジュール策定',
    description: '2026年3月の月次セキュリティパッチ適用計画を策定する。対象: EC2 15台、RDS 3台。メンテナンスウィンドウの確認が必要。',
    status: 'waiting',
    priority: 'medium',
    category: 'maintenance',
    assignee: '高橋美咲',
    reporter: '佐藤花子',
    relatedIncidentId: null,
    tags: ['patch', 'security', 'maintenance-window'],
    comments: [
      { id: 'cmt-004', author: '高橋美咲', content: '各チームへメンテナンスウィンドウの確認メールを送信しました。返答待ちです。', createdAt: '2026-02-23T11:00:00Z' },
    ],
    dueDate: '2026-03-01T00:00:00Z',
    createdAt: '2026-02-22T10:00:00Z',
    updatedAt: '2026-02-23T11:00:00Z',
    resolvedAt: null,
  },
  {
    id: id<TicketId>('tkt-005'),
    ticketNumber: 'TKT-2026-0005',
    title: 'デプロイ自動化スクリプト更新',
    description: 'CI/CD パイプラインのデプロイスクリプトを更新し、ブルーグリーンデプロイメントの自動切り替え機能を追加する。',
    status: 'resolved',
    priority: 'low',
    category: 'toil_reduction',
    assignee: '田中一郎',
    reporter: '田中一郎',
    relatedIncidentId: null,
    tags: ['ci-cd', 'automation', 'deploy'],
    comments: [
      { id: 'cmt-005', author: '田中一郎', content: '実装完了。ステージング環境でテスト済みです。', createdAt: '2026-02-23T16:00:00Z' },
      { id: 'cmt-006', author: '山田太郎', content: 'LGTM。本番環境にも適用してください。', createdAt: '2026-02-23T17:00:00Z' },
    ],
    dueDate: '2026-02-25T00:00:00Z',
    createdAt: '2026-02-20T09:00:00Z',
    updatedAt: '2026-02-23T17:00:00Z',
    resolvedAt: '2026-02-23T17:00:00Z',
  },
  {
    id: id<TicketId>('tkt-006'),
    ticketNumber: 'TKT-2026-0006',
    title: 'IAM ポリシー棚卸し',
    description: '四半期ごとの IAM ポリシー棚卸しを実施。未使用ロール・過剰権限の特定と是正を行う。',
    status: 'open',
    priority: 'high',
    category: 'security',
    assignee: '佐藤花子',
    reporter: '高橋美咲',
    relatedIncidentId: null,
    tags: ['iam', 'security', 'quarterly-review'],
    comments: [],
    dueDate: '2026-03-15T00:00:00Z',
    createdAt: '2026-02-23T08:00:00Z',
    updatedAt: '2026-02-23T08:00:00Z',
    resolvedAt: null,
  },
  {
    id: id<TicketId>('tkt-007'),
    ticketNumber: 'TKT-2026-0007',
    title: 'ランブック：DB フェイルオーバー手順書更新',
    description: 'RDS Multi-AZ 構成のフェイルオーバー手順書を最新のインフラ構成に合わせて更新する。',
    status: 'closed',
    priority: 'low',
    category: 'documentation',
    assignee: '鈴木次郎',
    reporter: '山田太郎',
    relatedIncidentId: null,
    tags: ['runbook', 'rds', 'documentation'],
    comments: [
      { id: 'cmt-007', author: '鈴木次郎', content: '手順書を更新し、Confluence にも反映しました。', createdAt: '2026-02-21T15:00:00Z' },
    ],
    dueDate: '2026-02-22T00:00:00Z',
    createdAt: '2026-02-19T10:00:00Z',
    updatedAt: '2026-02-21T15:00:00Z',
    resolvedAt: '2026-02-21T15:00:00Z',
  },
  {
    id: id<TicketId>('tkt-008'),
    ticketNumber: 'TKT-2026-0008',
    title: 'バッチ処理ログローテーション設定',
    description: 'batch-processor のディスク使用量抑制のため、ログローテーション設定を追加する。logrotate + S3 アーカイブ。',
    status: 'in_progress',
    priority: 'medium',
    category: 'maintenance',
    assignee: '山田太郎',
    reporter: '鈴木次郎',
    relatedIncidentId: null,
    tags: ['batch', 'logging', 'disk'],
    comments: [
      { id: 'cmt-008', author: '山田太郎', content: 'logrotate の設定ファイルを作成中です。', createdAt: '2026-02-24T09:00:00Z' },
    ],
    dueDate: '2026-02-26T00:00:00Z',
    createdAt: '2026-02-23T14:00:00Z',
    updatedAt: '2026-02-24T09:00:00Z',
    resolvedAt: null,
  },
];
